import type { Resource } from 'fhir/r4';
import PractitionerCDO from 'models/practitioner';
import type React from 'react';
import { useState } from 'react';
import { Header, Button, Icon, Modal, Form, Input, Select, Message, Dimmer, Loader } from 'semantic-ui-react';

import { useAuthContext } from '../../../contexts/AuthContext';
import FHIRUpdater from '../../../data/FHIRUpdater';
import { PractitionerBuilder } from '../../../fhir-sdoh';

const genderOptions = [
  { key: 'm', text: 'Male', value: 'Male' },
  { key: 'f', text: 'Female', value: 'Female' },
  { key: 'na', text: 'Unknown', value: 'Unknown' },
];

/**
 * What `components/home/members/index.tsx` — the only call site — passes.
 *
 * `toggleModal` is declared to take a second argument because THIS component calls it
 * with one (`setFlag(false, response)` after a successful save), even though its
 * producer there (`togglePractitionerModal`) accepts only the flag. The extra argument
 * is ignored at runtime; the optional parameter records the mismatch rather than
 * hiding it behind `any`.
 *
 * `practitionersData` is passed by the call site and never read here.
 */
interface EditCCProps {
  open: boolean;
  operation: string;
  organization: string;
  toggleModal: (flag: boolean, fhirPractitioner?: Resource) => void;
  practitionersData?: unknown;
  setNotification: (value: boolean) => void;
  setNotificationMsg: (value: string) => void;
}

// UI12: modal flag 'open' passed here from PractitionerCard
function EditCC(props: EditCCProps) {
  const authInfo = useAuthContext();

  const [dimmerProcess, setDimmerProcess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const open = props.open;
  const setFlag = props.toggleModal;
  const operation = props.operation;

  // Object template for practitioner builder to create Practitioner object
  const [practitionerDetails, setPractitionerDetails] = useState(PractitionerCDO);

  // `e.target` is read for `name`/`value` (an `<input>`) and for `textContent` (the
  // selected `<div>` inside semantic-ui's `Select`), so the element type differs per
  // branch — which is why this reads off `HTMLElement` and narrows per use.
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, attributeName?: string) => {
    if (e && e.target) {
      let name = e.target.name;
      let value = '';
      if (attributeName === 'gender') {
        name = attributeName;
        value = e.target.textContent;
      } else {
        value = e.target.value;
      }
      setPractitionerDetails((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  //   FHIR Call to update practitioner details
  const savePractitioner = () => {
    setDimmerProcess(true);
    // NOTE: this loop is left untyped. `practitionerDetails[property]` is a string
    // index into a closed-key object literal, and every spelling that satisfies the
    // compiler either adds a binding (which emits) or needs `as unknown as string[]`
    // for the dead `Array.isArray` arm — `PractitionerCDO`'s fields are all `string`,
    // so that arm can never run. Typing it honestly means first deciding whether to
    // delete the dead arm, which is a behaviour change.
    const fields = practitionerDetails as unknown as Record<string, unknown>;
    for (const property in fields) {
      const value = fields[property];
      if (Array.isArray(value) && value[0] !== undefined) {
        value[0] = (value[0] as string).trim();
      } else if (typeof value === 'string' && value !== undefined) {
        fields[property] = value.trim();
      }
    }

    const practitionerPayLoad = PractitionerBuilder.build(
      props.organization,
      'CC', //type
      {
        first: !practitionerDetails.middleName
          ? `${practitionerDetails.firstName}`
          : `${practitionerDetails.firstName},${practitionerDetails.middleName}`, //if middle name exist, need to add as index 1 of first name array
        last: practitionerDetails.lastName,
      },
      practitionerDetails.email,
      practitionerDetails.workPhone,
      practitionerDetails?.gender,
      authInfo.practitionerName
    );

    FHIRUpdater('/Practitioner/' + practitionerPayLoad.id, authInfo.token, practitionerPayLoad).then(
      (response) => {
        setDimmerProcess(false);
        setFlag(false, response);
        props.setNotificationMsg('New practitioner has been added successfully...');
        props.setNotification(true);
      },
      (_error) => {
        setDimmerProcess(false);
        props.setNotificationMsg('Unable to create practitioner details...Please check logs');
        props.setNotification(true);
        setValidationErrors((state) => [...state, 'Unable to create practitioner details...Please check logs']);
      }
    );
  };

  return (
    <div>
      <Modal
        closeOnEscape={false}
        closeOnDimmerClick={false}
        open={open}
        onClose={() => setFlag(false)}
        onOpen={() => setFlag(true)}
      >
        <Header icon="edit" color="red" content={operation + ' Care Coordinator Practitioner Details'} />
        {validationErrors.length > 0 && (
          <Message error>
            <Message.Header>Error:</Message.Header>
            <Message.List>{validationErrors}</Message.List>
          </Message>
        )}

        <Modal.Content>
          <Form className="practitionerForm" onSubmit={savePractitioner} autoComplete="off" error>
            <Form.Group>
              <Form.Field
                id="form-input-control-first-name"
                control={Input}
                label="First Name"
                width={8}
                placeholder="First Name"
                name="firstName"
                value={practitionerDetails.firstName}
                onChange={handleChange}
                required
              />
              <Form.Field
                id="form-input-control-middle-name"
                control={Input}
                width={2}
                label="Middle Name"
                placeholder="Middle Name"
                name="middleName"
                value={practitionerDetails.middleName}
                onChange={handleChange}
              />
              <Form.Field
                id="form-input-control-last-name"
                control={Input}
                width={6}
                label="Last Name"
                placeholder="Last Name"
                name="lastName"
                value={practitionerDetails.lastName}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group widths="equal">
              <Form.Field
                id="form-input-control-organization"
                control={Input}
                label="Organization"
                placeholder="Organization"
                name="org"
                value={props.organization}
                // value={practitionerDetails.org}
                // options={[{ text: 'All', key: 'All', value: 'All' }].concat(
                //   authInfo?.payers?.map((x) => {
                //     return { key: x, text: x, value: x };
                //   })
                // )}
                // onChange={handleChange}
                readOnly
                required
              />
              <Form.Field
                id="form-select-control-gender"
                control={Select}
                options={genderOptions}
                label={{
                  children: 'Gender',
                  htmlFor: 'form-select-control-gender',
                }}
                placeholder="Gender"
                name="gender"
                value={practitionerDetails.gender}
                onChange={(e) => handleChange(e as React.ChangeEvent<HTMLInputElement>, 'gender')}
                search
                searchInput={{ id: 'form-select-control-gender' }}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Field
                id="form-input-control-mobile-phone"
                control={Input}
                label="Work Phone"
                placeholder="Work Phone"
                name="workPhone"
                value={practitionerDetails.workPhone}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Field
                id="form-input-control-email"
                control={Input}
                label="Email"
                placeholder="Email"
                name="email"
                value={practitionerDetails.email}
                onChange={handleChange}
              />
            </Form.Group>

            <Modal.Actions style={{ textAlign: 'center' }}>
              <Button color="red" onClick={() => setFlag(false)}>
                <Icon name="remove" /> Cancel
              </Button>
              <Button color="green" type="submit">
                <Icon name="checkmark" /> {operation}
              </Button>
            </Modal.Actions>
          </Form>
          <Dimmer active={dimmerProcess}>
            <Loader />
          </Dimmer>
        </Modal.Content>
      </Modal>
    </div>
  );
}

export default EditCC;
